import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ScanLine, X } from "lucide-react";
import { toast } from "sonner";

/**
 * BarcodeScannerDialog — opens the tablet camera, decodes EAN/UPC/Code128 barcodes,
 * and resolves a single barcode string via `onDetected(code)` then closes.
 *
 * Falls back to a manual entry input below the camera if the device blocks camera.
 */
export default function BarcodeScannerDialog({ open, onClose, onDetected }) {
  const containerId = "barcode-scanner-region";
  const scannerRef = useRef(null);
  const [manual, setManual] = useState("");
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setStarting(true);

    const start = async () => {
      try {
        const node = document.getElementById(containerId);
        if (!node) return;
        const scanner = new Html5Qrcode(containerId, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 12, qrbox: { width: 280, height: 160 } },
          (decoded) => {
            if (!alive) return;
            try { onDetected(decoded.trim()); } finally { stop(); }
          },
          () => { /* per-frame fail ignored */ }
        );
      } catch (e) {
        toast.error("Camera unavailable — enter the barcode manually");
      } finally {
        if (alive) setStarting(false);
      }
    };

    const stop = async () => {
      try {
        if (scannerRef.current) {
          await scannerRef.current.stop();
          await scannerRef.current.clear();
        }
      } catch { /* noop */ }
      scannerRef.current = null;
    };

    start();
    return () => {
      alive = false;
      stop();
    };
  }, [open, onDetected]);

  const submitManual = () => {
    const v = manual.trim();
    if (!v) return;
    onDetected(v);
    setManual("");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5" />
            Scan Barcode · Σάρωση Barcode
          </DialogTitle>
        </DialogHeader>
        <div
          id={containerId}
          className="aspect-video w-full bg-slate-900 rounded-lg overflow-hidden flex items-center justify-center text-slate-400 text-sm"
          data-testid="barcode-camera-region"
        >
          {starting && "Starting camera…"}
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            Or type / paste the barcode
          </Label>
          <div className="flex gap-2">
            <Input
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitManual()}
              placeholder="e.g. 5901234123457"
              data-testid="barcode-manual-input"
              className="font-mono min-h-[44px]"
            />
            <Button onClick={submitManual} disabled={!manual.trim()} data-testid="barcode-manual-submit">
              Use
            </Button>
          </div>
          <Button variant="ghost" onClick={onClose} className="w-full mt-1" data-testid="barcode-close-btn">
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
